import { defineComponent, h, nextTick } from 'vue';
import { QBtn, QInput, QSelect, QIcon } from 'quasar';
import { Container, Draggable } from 'vue3-smooth-dnd';
import utils from '../../../Shared/utils.jsx';
import util from '../../careHelpfulFunctions.jsx';

const SPLITTER_LS_PREFIX = 'schemaProperties.splitter.';
const SPLIT_MIN_PCT = 12;
const SPLIT_MAX_PCT = 88;

/** Keys shown in the schema field list (same rule as render). */
function getVisiblePropertyKeys(obj) {
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj).filter((k) => k[0] !== '$');
}

/**
 * Rebuild properties object: $-prefixed keys first (original order), then visible keys in new order.
 */
function rebuildSchemaPropertiesObject(obj, newVisibleKeyOrder) {
    if (!obj || typeof obj !== 'object') return obj;
    const allKeys = Object.keys(obj);
    const metaKeys = allKeys.filter((k) => k[0] === '$');
    const visible = allKeys.filter((k) => k[0] !== '$');
    if (newVisibleKeyOrder.length !== visible.length) return obj;
    const visSet = new Set(visible);
    for (let i = 0; i < newVisibleKeyOrder.length; i++) {
        if (!visSet.has(newVisibleKeyOrder[i])) return obj;
    }
    const out = {};
    for (let i = 0; i < metaKeys.length; i++) {
        const k = metaKeys[i];
        out[k] = obj[k];
    }
    for (let i = 0; i < newVisibleKeyOrder.length; i++) {
        const k = newVisibleKeyOrder[i];
        out[k] = obj[k];
    }
    return out;
}

function moveVisibleProperty(obj, name, delta) {
    const vis = getVisiblePropertyKeys(obj);
    const i = vis.indexOf(name);
    if (i < 0) return obj;
    const j = i + delta;
    if (j < 0 || j >= vis.length) return obj;
    const next = vis.slice();
    const t = next[i];
    next[i] = next[j];
    next[j] = t;
    return rebuildSchemaPropertiesObject(obj, next);
}

function schemaSplitterStorageKey(schema, schemakey) {
    const id = schema && schema.Id != null ? String(schema.Id) : '';
    const sk = schemakey != null && schemakey !== '' ? String(schemakey) : '';
    return SPLITTER_LS_PREFIX + (id || sk || 'default');
}

const SchemaItem = defineComponent({
    name: 'schema-item-2',
    components: {
        QBtn,
        QInput,
        QSelect,
        QIcon,
    },
    data: () => ({
        schema: null,
        list: null,
        localvalue: null,
        localName: '',
    }),
    props: {
        name: null,
        cmodel: null,
        typeSchema: null,
        renamed: null,
        changed: null,
        deleted: null,
        moveup: null,
        movedown: null,
        selected: null,
        setselection: null,
        navigateTo: null,
        canNavigate: false,
        propertygrid: false,
        onSchemaSplitPointerDown: null,
        /** Stable id from property value `$objectId`; used with `focusNameForObjectId` after Add Another. */
        rowObjectId: null,
        /** When equal to this row's `rowObjectId`, focus the property name field (parent clears after emit). */
        focusNameForObjectId: null,
    },
    emits: ['nameFocusApplied'],
    watch: {
        focusNameForObjectId() {
            this.tryFocusPropertyNameField();
        },
        list() {
            this.tryFocusPropertyNameField();
        },
    },
    async created() {
        if (this.typeSchema) {
            const s = await utils.schema.get(this.typeSchema);
            this.schema = utils.schema.resolve_Of(s);
        }
        this.list = await utils.schema.get('/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/SchemaSchema');

        if (this.schema)
            for (let i = 0; i < this.list.anyOf.length; i++) {
                const opt = this.list.anyOf[i];

                if (this.schema && this.schema.title === opt.title) {
                    this.localvalue = opt.Id;
                }
            }
        this.localName = this.name ?? '';
    },
    methods: {
        onChanged(name, value) {
            let strvalue;
            if (typeof value === 'string') strvalue = value;
            else strvalue = JSON.stringify(value);

            console.log(`SchemaProperties2 changed; name:${name}, value:${strvalue}`);
            this.localvalue = value;
            this.changed(name, value);
        },
        onPropertyNameKeydown(e) {
            if (e.key !== 'Tab' || e.shiftKey) return;
            e.preventDefault();
            nextTick(() => {
                this.$refs.propertyTypeSelect?.focus();
            });
        },
        tryFocusPropertyNameField() {
            const v = this.focusNameForObjectId;
            if (v == null || this.rowObjectId == null || !this.list) {
                this._nameFocusRetryCount = 0;
                return;
            }
            if (String(v) !== String(this.rowObjectId)) {
                this._nameFocusRetryCount = 0;
                return;
            }
            nextTick(() => {
                const inp = this.$refs.propertyNameInput;
                if (!inp) {
                    this._nameFocusRetryCount = (this._nameFocusRetryCount || 0) + 1;
                    if (this._nameFocusRetryCount > 15) {
                        this._nameFocusRetryCount = 0;
                        this.$emit('nameFocusApplied');
                        return;
                    }
                    nextTick(() => this.tryFocusPropertyNameField());
                    return;
                }
                this._nameFocusRetryCount = 0;
                if (typeof inp.focus === 'function') inp.focus();
                const native = inp.$el?.querySelector?.('input');
                if (native && typeof native.select === 'function') native.select();
                this.$emit('nameFocusApplied');
            });
        },
    },
    render() {
        if (!this.list) return null;

        const options = [];
        for (let i = 0; i < this.list.anyOf.length; i++) {
            const opt = this.list.anyOf[i];
            options.push({ text: opt.title, value: opt.Id, disabled: false });
        }
        options.sort((a, b) => a.text.localeCompare(b.text));

        if (!this.localvalue) options.splice(0, 0, { text: 'Please select one', value: '', disabled: true });

        let select_checkbox;
        let navigate;
        if (this.canNavigate && this.typeSchema) {
            select_checkbox = (
                <input
                    type="checkbox"
                    checked={this.name in this.selected}
                    onChange={(e) => this.setselection(this.name, e.target.checked)}
                />
            );
            navigate = (
                <QBtn
                    flat
                    size="sm"
                    padding="xs sm"
                    color="blue-4"
                    icon="mdi-open-in-new"
                    title="Edit"
                    onClick={() => this.navigateTo(this, this.name)}
                />
            );
        }

        const btn_styles = {
            'font-size': 'x-small',
        };

        const onSplit = this.onSchemaSplitPointerDown;
        const gutter =
            typeof onSplit === 'function' ? (
                <div
                    class="c-PropertyGrid2__gutter c-SchemaProperties__gutter"
                    aria-hidden="true"
                    onPointerdown={(e) => onSplit(e, e.currentTarget)}
                />
            ) : (
                <div class="c-SchemaProperties__gutterSpacer" aria-hidden="true" />
            );

        return (
            <div class="c-PropertyGrid2__schemaRow c-SchemaProperties__schemaRow">
                <div class="c-SchemaProperties__nameCell">
                    <span
                        class="property-grid"
                        style={{ whiteSpace: 'nowrap', display: 'flex', flexDirection: 'row', alignItems: 'center', minWidth: 0 }}
                    >
                        {select_checkbox}
                        {navigate}
                        <QInput
                            ref="propertyNameInput"
                            style={{ flexGrow: 1, minWidth: 0 }}
                            borderless
                            dense
                            hide-bottom-space
                            modelValue={this.localName}
                            onUpdate:modelValue={(value) => (this.localName = value)}
                            onBlur={() => this.renamed(this.name, this.localName)}
                            onKeydown={this.onPropertyNameKeydown}
                        />
                    </span>
                </div>
                {gutter}
                <div class={{ 'c-PropertyGrid2__value': true, 'property-grid': true, 'c-SchemaProperties__typeCell': true }}>
                    <QSelect
                        ref="propertyTypeSelect"
                        borderless
                        dense
                        hide-bottom-space
                        style={{ width: '100%', minWidth: 0 }}
                        popup-content-class="q-select-max-popup-height"
                        options={options}
                        option-label="text"
                        option-value="value"
                        options-dense
                        emit-value
                        map-options
                        modelValue={this.localvalue}
                        onUpdate:modelValue={(value) => this.onChanged(this.name, value)}
                    />
                </div>
                <div class="property-grid c-SchemaProperties__actionsCell" style={{ whiteSpace: 'nowrap', minWidth: 0 }}>
                    <QBtn v-show={false}
                        flat
                        size="sm"
                        dense
                        padding="xs xs"
                        color="grey-8"
                        icon="mdi-arrow-up"
                        title="Move Up"
                        style={btn_styles}
                        onClick={() => this.moveup(this.name)}
                    />
                    <QBtn v-show={false}
                        flat
                        size="sm"
                        dense
                        padding="xs xs"
                        color="grey-8"
                        icon="mdi-arrow-down"
                        title="Move Down"
                        style={btn_styles}
                        onClick={() => this.movedown(this.name)}
                    />
                    <QBtn
                        flat
                        size="sm"
                        dense
                        class="q-mr-md"
                        xpadding="xs xs"
                        color="grey-8"
                        icon="mdi-close"
                        title="Delete"
                        style={btn_styles}
                        onClick={() => this.deleted(this.name)}
                    />
                    <span
                        class="column-drag-handle schema-properties-drag-handle"
                        title="Drag to reorder"
                        style={{ cursor: 'grab', flexShrink: 0, padding: '0 2px 0 4px', userSelect: 'none' }}
                    >
                        &#x2630;
                    </span>
                </div>
            </div>
        );
    },
});

export default defineComponent({
    name: 'schema-properties-2',
    components: {
        QBtn,
        QIcon,
        SchemaItem,
        Container,
        Draggable,
    },
    data: () => ({
        selected: {},
        labelPct: 50,
        _splitMove: null,
        /** Set when Add Another creates a row so that row's name field can autofocus. */
        focusNameRowObjectId: null,
    }),
    computed: {
        ...utils.forms.computed,
        anySelected: function () {
            return Object.keys(this.selected).length > 0;
        },
    },
    props: {
        name: '',
        schema: null,
        schemakey: null,
        root: null,
        cmodel: null,
        navigate: null,
        canNavigate: false,
        propertygrid: false,
    },
    created() {
        const schema = this.schema && utils.schema.resolve_Of(this.schema);
        const raw = localStorage.getItem(schemaSplitterStorageKey(schema, this.schemakey));
        if (raw != null) {
            const n = parseFloat(raw);
            if (!Number.isNaN(n)) this.labelPct = Math.min(SPLIT_MAX_PCT, Math.max(SPLIT_MIN_PCT, n));
        }
    },
    unmounted() {
        this.teardownSplitDrag();
    },
    methods: {
        async itemChanged(prop, value) {
            let s = await utils.schema.get(value);
            s = utils.schema.resolve_Of(s);
            const m = utils.schema.getDefaultModel(s);
            this.cmodel[this.schemakey][prop] = m;
        },
        itemRenamed(prop, value) {
            if (prop == value) return;

            const oldobj = this.cmodel[this.schemakey];
            if (value in oldobj) {
                alert('Property ' + value + ' is already used');
                return;
            }
            const properties = util.renameProperty(oldobj, prop, value);
            this.cmodel[this.schemakey] = properties;
        },
        itemDeleted(prop) {
            delete this.cmodel[this.schemakey][prop];
        },
        addAnother() {
            const properties = this.cmodel[this.schemakey];
            let key = 'Unnamed';
            let dup = 1;
            while (key in properties) key = 'Unnamed_' + dup++;

            const id = utils.generateUUID();
            properties[key] = { $objectId: id };
            this.focusNameRowObjectId = id;
        },
        onSchemaItemNameFocusApplied() {
            this.focusNameRowObjectId = null;
        },
        moveDown(prop) {
            const properties = this.cmodel[this.schemakey];
            if (!properties) return;
            this.cmodel[this.schemakey] = moveVisibleProperty(properties, prop, 1);
        },
        moveUp(prop) {
            const properties = this.cmodel[this.schemakey];
            if (!properties) return;
            this.cmodel[this.schemakey] = moveVisibleProperty(properties, prop, -1);
        },
        reOrderItems(res) {
            if (res.removedIndex == null || res.addedIndex == null) return;
            const properties = this.cmodel[this.schemakey];
            if (!properties) return;
            const vis = getVisiblePropertyKeys(properties);
            const keys = vis.slice();
            const item = keys[res.removedIndex];
            keys.splice(res.removedIndex, 1);
            keys.splice(res.addedIndex, 0, item);
            this.cmodel[this.schemakey] = rebuildSchemaPropertiesObject(properties, keys);
        },
        persistSchemaSplitterPct() {
            try {
                const schema = this.schema && utils.schema.resolve_Of(this.schema);
                localStorage.setItem(schemaSplitterStorageKey(schema, this.schemakey), String(this.labelPct));
            } catch (_) {
                /* ignore */
            }
        },
        teardownSplitDrag() {
            if (!this._splitMove) return;
            const { move, up, captureTarget, pointerId } = this._splitMove;
            if (captureTarget && typeof captureTarget.releasePointerCapture === 'function' && pointerId != null) {
                try {
                    if (captureTarget.hasPointerCapture?.(pointerId)) captureTarget.releasePointerCapture(pointerId);
                } catch (_) {
                    /* ignore */
                }
            }
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('pointercancel', up);
            this._splitMove = null;
        },
        onSchemaSplitPointerDown(e, gutterEl) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            const root = this.$refs.schemaSplitRoot;
            if (!root) return;

            const target = gutterEl || e.currentTarget;
            const pointerId = e.pointerId;
            if (target && typeof target.setPointerCapture === 'function' && pointerId != null) {
                try {
                    target.setPointerCapture(pointerId);
                } catch (_) {
                    /* ignore */
                }
            }

            const startX = e.clientX;
            const startPct = this.labelPct;
            const move = (ev) => {
                const rect = root.getBoundingClientRect();
                if (rect.width <= 0) return;
                let pct = startPct + ((ev.clientX - startX) / rect.width) * 100;
                pct = Math.min(SPLIT_MAX_PCT, Math.max(SPLIT_MIN_PCT, pct));
                this.labelPct = pct;
            };
            const up = () => {
                this.teardownSplitDrag();
                this.persistSchemaSplitterPct();
            };
            this._splitMove = { move, up, captureTarget: target, pointerId };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            window.addEventListener('pointercancel', up);
            move(e);
        },
        setSelection(name, checked) {
            if (checked) this.selected[name] = checked;
            else delete this.selected[name];
        },
        async copy() {
            const properties = this.cmodel[this.schemakey];
            const selected = [];
            for (let key in this.selected) selected.push({ key: key, value: properties[key] });

            await utils.helpers.writeCareClipboard(selected);
        },
        async cut() {
            const properties = this.cmodel[this.schemakey];
            const selected = [];
            for (let key in this.selected) {
                selected.push({ key: key, value: properties[key] });
                delete properties[key];
            }

            await utils.helpers.writeCareClipboard(selected);

            this.selected = {};
        },
        async paste() {
            const data = await utils.helpers.readCareClipboardArray();
            if (!data) {
                alert('Failed to parse data from clipboard buffer');
                return;
            }
            if (!this.cmodel[this.schemakey]) this.cmodel[this.schemakey] = {};

            const properties = this.cmodel[this.schemakey];
            for (let i = 0; i < data.length; i++) {
                const item = data[i];
                if (!('key' in item && 'value' in item)) continue;

                let key = item.key;
                let dup = 1;
                while (key in properties) key = item.key + '_' + dup++;

                if (item.value.$objectId) item.value.$objectId = utils.generateUUID();

                properties[key] = item.value;
            }
        },
        selectAll() {
            const properties = this.cmodel[this.schemakey];
            const vis = getVisiblePropertyKeys(properties);
            for (let i = 0; i < vis.length; i++) this.selected[vis[i]] = true;
        },
        selectNone() {
            this.selected = {};
        },
    },
    render() {
        const cmodel = this.cmodel[this.schemakey];
        const draggableChildren = [];
        const visibleKeys = getVisiblePropertyKeys(cmodel);
        const splitHandler = this.onSchemaSplitPointerDown;

        for (let i = 0; i < visibleKeys.length; i++) {
            const key = visibleKeys[i];
            const item = cmodel[key];
            /* Stable row key so rename-on-blur does not remount the row (fixes Tab → type losing focus). */
            const rowKey = item && item.$objectId != null ? String(item.$objectId) : key;

            draggableChildren.push(
                <Draggable key={rowKey} index={i}>
                    <SchemaItem
                        name={key}
                        cmodel={cmodel}
                        typeSchema={item.$typeSchema}
                        rowObjectId={item.$objectId != null ? item.$objectId : null}
                        focusNameForObjectId={this.focusNameRowObjectId}
                        onNameFocusApplied={this.onSchemaItemNameFocusApplied}
                        changed={(prop, value) => this.itemChanged(prop, value)}
                        renamed={(prop, value) => this.itemRenamed(prop, value)}
                        deleted={(prop, value) => this.itemDeleted(prop, value)}
                        moveup={(prop) => this.moveUp(prop)}
                        movedown={(prop) => this.moveDown(prop)}
                        selected={this.selected}
                        setselection={(prop, checked) => this.setSelection(prop, checked)}
                        navigateTo={(control, prop) => this.navigate(control, prop, item)}
                        canNavigate={this.canNavigate}
                        propertygrid={this.propertygrid}
                        onSchemaSplitPointerDown={splitHandler}
                    />
                </Draggable>,
            );
        }

        const btn_styles = {
            'font-size': 'x-small',
        };
        const selections = (
            <span class="property-grid" style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                <QBtn
                    dense
                    size="sm"
                    class="q-px-sm"
                    color="grey-8"
                    text-color="white"
                    style={btn_styles}
                    disable={!this.anySelected}
                    onClick={this.copy}
                >
                    Copy
                </QBtn>
                <QBtn
                    dense
                    size="sm"
                    class="q-px-sm"
                    color="grey-8"
                    text-color="white"
                    style={btn_styles}
                    disable={!this.anySelected}
                    onClick={this.cut}
                >
                    Cut
                </QBtn>
                <QBtn dense size="sm" class="q-px-sm" color="grey-8" text-color="white" style={btn_styles} onClick={this.paste}>
                    Paste
                </QBtn>

                <QBtn dense size="sm" class="q-px-sm" color="grey-8" text-color="white" style={btn_styles} onClick={this.selectAll}>
                    Select All
                </QBtn>
                <QBtn dense size="sm" class="q-px-sm" color="grey-8" text-color="white" style={btn_styles} onClick={this.selectNone}>
                    Select None
                </QBtn>
            </span>
        );

        const splitRootStyle = {
            '--schema-label-pct': this.labelPct + '%',
            width: '100%',
            minWidth: 0,
        };

        return (
            <div
                ref="schemaSplitRoot"
                class="c-PropertyGrid2__schemaTable c-SchemaProperties__splitRoot"
                style={splitRootStyle}
            >
                <div style={{ padding: '4px' }}>{selections}</div>
                <div class="c-PropertyGrid2__schemaHeader c-SchemaProperties__schemaHeaderRow">
                    <div style={{ minWidth: 0 }}>Property Name</div>
                    <div />
                    <div style={{ minWidth: 0 }}>Property Type</div>
                    <div />
                </div>
                <Container onDrop={(res) => this.reOrderItems(res)} drag-handle-selector=".column-drag-handle">
                    {draggableChildren}
                </Container>
                <div class="property-grid" style={{ padding: '4px', minWidth: 0 }}>
                    <QBtn dense size="sm" padding="xs sm" color="grey-8" text-color="white" class="c-SentenceCaseButton" no-caps onClick={() => this.addAnother()} icon="mdi-plus">
                        <span>Add Another</span>
                    </QBtn>
                </div>
            </div>
        );
    },
});
